/**
 * Hook that combines Privy embedded wallet, Privy-connected external wallets,
 * and explicitly linked wallets into a unified list.
 *
 * Wallet labels are persisted server-side via the wallets API, authenticated
 * by the Privy wallet's EIP-712 signature.
 */
import { useCallback, useMemo } from 'react';
import { useWallets } from '@privy-io/react-auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAddress, getAddress } from 'viem';
import { useWalletRelations } from './useWalletRelations';
import { getWalletLabels, setWalletLabel as apiSetWalletLabel } from '@/lib/wallet-relations';
import { mapClientTypeToLabel, mapClientTypeToIcon } from '@/lib/wallet-utils';
import { WALLET_RELATIONS_EIP712_DOMAIN, EIP712_TYPES } from '@/types';
import { prepareWallet } from '@/lib/wallet-actions/prepare';
import { EIP712_DOMAIN_NAME_VERSION, signTypedData } from '@/lib/wallet-actions/sign';
import { ACTIONS } from '@/lib/wallet-actions/actions';
import type { UnifiedWallet } from '@/types/wallet';

export interface UseAllWalletsReturn {
  wallets: UnifiedWallet[];
  isLoading: boolean;
  /** Rename a wallet label (persisted server-side, signed by Privy wallet) */
  renameWallet: (address: string, newLabel: string) => Promise<boolean>;
}

export function useAllWallets(): UseAllWalletsReturn {
  const { wallets: privyWallets, ready } = useWallets();
  const queryClient = useQueryClient();
  const { linkedWallets, isLoading: relationsLoading } = useWalletRelations();

  // Find the Privy embedded wallet address for label queries
  const privyAddress = privyWallets.find((w) => w.walletClientType === 'privy')?.address ?? null;

  // Fetch server-side labels for the Privy wallet owner
  const { data: serverLabels = [] } = useQuery({
    queryKey: ['wallet-labels', privyAddress],
    queryFn: () => getWalletLabels(privyAddress!),
    enabled: !!privyAddress,
    staleTime: 1000 * 60 * 5,
  });

  const allWallets = useMemo(() => {
    const result: UnifiedWallet[] = [];
    const seen = new Set<string>();

    // 1. Privy embedded wallet (always first)
    const embeddedWallet = privyWallets.find((w) => w.walletClientType === 'privy');
    if (embeddedWallet) {
      const addr = embeddedWallet.address.toLowerCase();
      seen.add(addr);
      result.push({
        address: embeddedWallet.address,
        type: 'privy',
        label: 'Phenom Wallet',
        chains: ['base', 'polygon'],
        capabilities: {
          gaslessSend: true,
          signTypedData: true,
          nftTransfer: true,
        },
        iconType: 'privy',
        hasSigner: true,
      });
    }

    // 2. Connected external wallets (MetaMask, Coinbase, WalletConnect)
    for (const pw of privyWallets) {
      if (pw.walletClientType === 'privy') continue; // already added as embedded
      const addr = pw.address.toLowerCase();
      if (!seen.has(addr)) {
        seen.add(addr);
        result.push({
          address: pw.address,
          type: 'external',
          label: mapClientTypeToLabel(pw.walletClientType),
          chains: ['base', 'polygon'],
          capabilities: {
            gaslessSend: false,
            signTypedData: true,
            nftTransfer: true,
          },
          iconType: mapClientTypeToIcon(pw.walletClientType),
          hasSigner: true,
        });
      }
    }

    // 3. Wallets explicitly linked via wallet-relations (EIP-712 signatures)
    for (const linked of linkedWallets) {
      if (!isAddress(linked.address)) continue;
      const normalizedAddress = getAddress(linked.address);
      const addr = normalizedAddress.toLowerCase();
      if (seen.has(addr)) {
        // If a linked wallet matches an already-seen connected wallet,
        // update the existing entry's label if the linked wallet has a user-set label
        if (linked.label) {
          const existing = result.find((w) => w.address.toLowerCase() === addr);
          if (existing) {
            existing.label = linked.label;
          }
        }
        continue;
      }
      seen.add(addr);
      result.push({
        address: normalizedAddress,
        type: 'external',
        label: linked.label ?? 'Linked Wallet',
        chains: ['base', 'polygon'],
        capabilities: {
          gaslessSend: false,
          signTypedData: false,
          nftTransfer: false,
        },
        iconType: 'generic',
        hasSigner: false,
      });
    }

    // 4. Apply server-side label overrides
    for (const { address: labelAddr, label } of serverLabels) {
      const w = result.find((r) => r.address.toLowerCase() === labelAddr.toLowerCase());
      if (w) {
        w.label = label;
      }
    }

    return result;
  }, [privyWallets, linkedWallets, serverLabels]);

  /**
   * Rename a wallet by signing a SetLabel EIP-712 message with the Privy wallet.
   * The label is persisted server-side and syncs across browsers.
   */
  const renameWallet = useCallback(
    async (targetAddress: string, newLabel: string): Promise<boolean> => {
      const privyWallet = privyWallets.find((w) => w.walletClientType === 'privy');
      if (!privyWallet) return false;

      const ownerAddress = privyWallet.address;
      const timestamp = Math.floor(Date.now() / 1000);

      try {
        const signature = await signTypedData(
          await prepareWallet(privyWallet, ACTIONS.walletRelations()),
          {
            types: {
              EIP712Domain: EIP712_DOMAIN_NAME_VERSION,
              SetLabel: [...EIP712_TYPES.SetLabel],
            },
            primaryType: 'SetLabel',
            domain: WALLET_RELATIONS_EIP712_DOMAIN,
            message: {
              wallet: ownerAddress,
              targetWallet: targetAddress,
              label: newLabel,
              timestamp: BigInt(timestamp).toString(),
            },
          }
        );

        const result = await apiSetWalletLabel({
          wallet: ownerAddress,
          targetWallet: targetAddress,
          label: newLabel,
          timestamp,
          signature,
        });

        if (result.success) {
          void queryClient.invalidateQueries({ queryKey: ['wallet-labels', ownerAddress] });
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [privyWallets, queryClient]
  );

  return {
    wallets: allWallets,
    isLoading: !ready || relationsLoading,
    renameWallet,
  };
}
